import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { getListStores } from "../../../redux/actions/storeActions";
import {
  addNewSection,
  deleteSection,
  getListSections,
} from "../../../redux/actions/sectionActions";
import DefaultLayout from "../../../layouts/DefaultLayout";
import Loader from "../../../components/Loader";
import Alert from "../../../components/Alert";
import { baseURLFile } from "../../../constants";
import Select from "react-select";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../components/ConfirmationModal";
import Paginate from "../../../components/Paginate";

function SectionScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const page = searchParams.get("page") || "1";

  const [eventType, setEventType] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loadEvent, setLoadEvent] = useState(false);

  const [storeSelect, setStoreSelect] = useState("");
  const [storeSelectError, setStoreSelectError] = useState("");

  const [sectionName, setSectionName] = useState("");
  const [sectionNameError, setSectionNameError] = useState("");

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const listStores = useSelector((state) => state.getListStores);
  const { stores, loadingListStores, errorListStores } = listStores;

  const listSections = useSelector((state) => state.getListSection);
  const { sections, loadingListSection, errorListSection, pages } =
    listSections;

  const sectionAdd = useSelector((state) => state.createNewSection);
  const { loadingSectionAdd, errorSectionAdd, successSectionAdd } = sectionAdd;

  const sectionDelete = useSelector((state) => state.deleteSection);
  const { loadingSectionDelete, errorSectionDelete, successSectionDelete } =
    sectionDelete;

  const redirect = "/";

  useEffect(() => {
    if (!userInfo) {
      navigate(redirect);
    } else {
      dispatch(getListStores("0"));
      dispatch(getListSections(page));
    }
  }, [navigate, userInfo, dispatch, page]);

  useEffect(() => {
    if (successSectionAdd) {
      setStoreSelect("");
      setSectionName("");
      dispatch(getListStores("0"));
      dispatch(getListSections("1"));
    }
  }, [successSectionAdd]);

  useEffect(() => {
    if (successSectionDelete) {
      dispatch(getListStores("0"));
      dispatch(getListSections("1"));
    }
  }, [successSectionDelete]);

  return (
    <DefaultLayout>
      <div className="animate-fadeIn">
        {/* Modern Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <a
                href="/dashboard"
                className="inline-flex items-center text-sm font-medium text-primary hover:text-secondary transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  ></path>
                </svg>
                Dashboard
              </a>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-secondary"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <a
                  href="/stores"
                  className="ml-1 text-sm font-medium text-primary hover:text-secondary md:ml-2 transition-colors duration-200"
                >
                  Stores
                </a>
              </div>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-secondary"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <span className="ml-1 text-sm font-medium text-secondary md:ml-2">
                  Sections
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header with Store Image */}
        <div
          className="relative mb-8 rounded-xl overflow-hidden bg-gradient-to-r from-primary to-secondary shadow-lg"
          style={{
            backgroundImage: "url('/assets/background-auth-screen.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-secondary/80"></div>
          <div className="relative z-10 px-6 py-8 md:px-10 md:py-12">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Sections Management
            </h1>
            <p className="text-white/80 max-w-xl">
              Create and manage sections for your stores to organize products effectively.
            </p>
          </div>
        </div>

        {/* Form Section Title */}
        <div className="flex flex-row justify-between items-center mb-6">
          <div className="text-xl font-bold text-black">Create New Section</div>
        </div>

        <div className="rounded-xl border border-stroke bg-whiter px-6 pt-6 pb-5 shadow-sm hover:shadow-md transition-all duration-300 mb-8">
          <div className="container mx-auto flex flex-col">
            <div className="flex md:flex-row flex-col gap-6">
              {/* Store Selection */}
              <div className="md:w-1/2 w-full">
                <label className="flex items-center text-sm font-medium text-body mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-bodydark">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z" />
                  </svg>
                  Select Store <span className="text-danger ml-1">*</span>
                </label>
                <div>
                  <Select
                    value={storeSelect}
                    onChange={(option) => {
                      setStoreSelect(option);
                    }}
                    options={stores?.map((store) => ({
                      value: store.id,
                      label: store.store_name,
                    }))}
                    filterOption={(option, inputValue) =>
                      option.label
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                    }
                    className="text-sm"
                    placeholder="Select a Store..."
                    isSearchable
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        background: "#fff",
                        borderRadius: '0.5rem',
                        padding: '2px',
                        border: storeSelectError
                          ? "2px solid #d34053"
                          : state.isFocused
                            ? "2px solid #0D9F61"
                            : "2px solid #e2e8f0",
                        boxShadow: state.isFocused ? "0 0 0 1px rgba(13, 159, 97, 0.1)" : "none",
                        "&:hover": {
                          border: state.isFocused
                            ? "2px solid #0D9F61"
                            : "2px solid #cbd5e1",
                        },
                        transition: 'all 0.2s ease',
                      }),
                      option: (base, state) => ({
                        ...base,
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: state.isSelected
                          ? 'rgba(13, 159, 97, 0.1)'
                          : state.isFocused
                            ? 'rgba(13, 159, 97, 0.05)'
                            : 'transparent',
                        color: state.isSelected ? '#0D9F61' : base.color,
                        '&:hover': {
                          backgroundColor: 'rgba(13, 159, 97, 0.05)',
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        display: "flex",
                        alignItems: "center",
                        color: '#1e293b',
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: '#94a3b8',
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        color: '#94a3b8',
                        '&:hover': {
                          color: '#0D9F61',
                        },
                      }),
                    }}
                  />
                  {storeSelectError && (
                    <p className="mt-1.5 text-xs text-danger flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {storeSelectError}
                    </p>
                  )}
                </div>
              </div>

              {/* Section Name Input */}
              <div className="md:w-1/2 w-full">
                <label className="flex items-center text-sm font-medium text-body mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-bodydark">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
                  </svg>
                  Section Name <span className="text-danger ml-1">*</span>
                </label>
                <div>
                  <input
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      sectionNameError
                        ? "border-danger bg-danger/5 focus:border-danger"
                        : "border-stroke focus:border-primary bg-white"
                    } outline-none focus:ring-2 ${
                      sectionNameError ? "focus:ring-danger/20" : "focus:ring-primary/20"
                    } text-sm transition-all duration-200`}
                    type="text"
                    placeholder="Enter section name"
                    value={sectionName}
                    onChange={(v) => setSectionName(v.target.value)}
                  />
                  {sectionNameError && (
                    <p className="mt-1.5 text-xs text-danger flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {sectionNameError}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6">
              <div className="flex flex-row items-center justify-end gap-3">
                <button
                  onClick={async () => {
                    setStoreSelectError("");
                    setStoreSelect("");
                    setSectionNameError("");
                    setSectionName("");
                  }}
                  className="px-5 py-2.5 rounded-lg border-2 border-stroke text-bodydark font-medium text-sm hover:bg-gray-1 transition-colors duration-200 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
                  </svg>
                  Reset
                </button>
                <button
                  onClick={async () => {
                    var check = true;
                    setStoreSelectError("");
                    setSectionNameError("");

                    if (!storeSelect || !storeSelect.value) {
                      setStoreSelectError("This field is required.");
                      check = false;
                    }

                    if (sectionName === "") {
                      setSectionNameError("This field is required.");
                      check = false;
                    }

                    if (check) {
                      await dispatch(
                        addNewSection({
                          section_name: sectionName,
                          section_store: storeSelect?.value,
                        })
                      ).then(() => {});
                    } else {
                      toast.error(
                        "Some fields are empty or invalid. Please try again."
                      );
                    }
                  }}
                  className="px-5 py-2.5 rounded-lg bg-primary text-white font-medium text-sm hover:bg-primary/90 transition-colors duration-200 shadow-sm hover:shadow-md flex items-center"
                  disabled={loadingSectionAdd}
                >
                  {loadingSectionAdd ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                      Create Section
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Sections List Header */}
        <div className="flex flex-row justify-between items-center mb-6">
          <div className="text-xl font-bold text-black">Sections List</div>
        </div>

        {/* Modern Table Card */}
        <div className="rounded-xl border border-stroke bg-whiter px-6 pt-6 pb-5 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="container mx-auto flex flex-col">
            {loadingListSection ? (
              <div className="flex justify-center items-center h-80">
                <Loader />
              </div>
            ) : errorListSection ? (
              <div className="flex justify-center items-center h-80">
                <Alert message={errorListSection} />
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
                <table className="w-full table-auto">
                  <thead>
                    <tr className="bg-gray-2 text-left">
                      <th className="min-w-[80px] py-4 px-4 font-semibold text-black text-sm rounded-l-lg">
                        #
                      </th>
                      <th className="min-w-[180px] py-4 px-4 font-semibold text-black text-sm">
                        Section Name
                      </th>
                      <th className="min-w-[180px] py-4 px-4 font-semibold text-black text-sm">
                        Store Name
                      </th>
                      <th className="min-w-[120px] py-4 px-4 font-semibold text-black text-sm">
                        Store Logo
                      </th>
                      <th className="min-w-[100px] py-4 px-4 font-semibold text-black text-sm rounded-r-lg text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sections?.map((item, index) => (
                      <tr
                        key={index}
                        className={`hover:bg-gray-1 transition-colors duration-200 group ${
                          index === sections.length - 1 ? "" : "border-b border-stroke"
                        }`}
                      >
                        <td className="py-4 px-4">
                          <span className="text-sm font-medium text-black bg-gray-2 py-1 px-2.5 rounded-md">
                            #{item.id}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <h5 className="text-sm font-medium text-black">
                            {item.section_name ?? "---"}
                          </h5>
                        </td>
                        <td className="py-4 px-4">
                          <h5 className="text-sm font-medium text-black">
                            {item.store?.store_name ?? "---"}
                          </h5>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            <img
                              src={baseURLFile + item.store?.store_logo}
                              alt={item.store?.store_name || "Store logo"}
                              className="size-12 rounded-lg border-2 border-white shadow-sm object-cover"
                            />
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() => {
                                setEventType("delete");
                                setSectionId(item.id);
                                setIsOpen(true);
                              }}
                              className="hover:text-danger text-body p-1.5 rounded-full hover:bg-danger/10 transition-colors duration-200"
                              title="Delete Section"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="mt-6">
                  <Paginate
                    route={"/sections?"}
                    search={""}
                    page={page}
                    pages={pages}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Enhanced Confirmation Modal */}
        <ConfirmationModal
          isOpen={isOpen}
          message={
            eventType === "delete"
              ? "Are you sure you want to delete this section? This action cannot be undone."
              : "Are you sure?"
          }
          onConfirm={async () => {
            if (eventType === "cancel") {
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
              setSectionId("");
            } else if (eventType === "delete" && sectionId !== "") {
              setLoadEvent(true);
              dispatch(deleteSection(sectionId));
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
            } else {
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
              setSectionId("");
            }
          }}
          onCancel={() => {
            setIsOpen(false);
            setEventType("");
            setLoadEvent(false);
            setSectionId("");
          }}
          loadEvent={loadEvent}
        />
      </div>
    </DefaultLayout>
  );
}

export default SectionScreen;
